import React, { HTMLAttributes, useEffect, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import PageSection from "components/layout/PageSection";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import Spacer from "components/Spacer";
import INewsArticle from "apiConsumers/newsApiConsumer/models/INewsArticle";
import { newsApi } from "apiConsumers";
import ArticleOverviewWithDescription from "components/news/articleOverviewWidgets/ArticleOverviewWithDescription";
import SubArticleWithTitle from "components/news/articleOverviewWidgets/SubArticleOverviewWithTitle";
import LoadableButton from "components/buttons/LoadableButton";
import Rotate from "components/rotate/Rotate";

const useStyles = makeStyles((theme: Theme) => {
    const subArticleSpacing = theme.spacing(4);

    return createStyles({
        root: {},
        content: {
            display: "flex",
            flexDirection: "column",
            flex: "1",
        },
        subArticleContainer: {
            display: "flex",
            flexWrap: "wrap",
            marginBottom: -subArticleSpacing,
            marginRight: -subArticleSpacing,
        },
        subArticle: {
            display: "flex",
            flex: "1",
            minWidth: 350,
            [theme.breakpoints.down("xs")]: {
                minWidth: 250,
            },
            marginRight: subArticleSpacing,
            marginBottom: subArticleSpacing,
        },
        loadMoreButtonContainer: {
            display: "flex",
            justifyContent: "center",
        },
    });
});

interface IProps extends HTMLAttributes<HTMLDivElement> {}

export default function NewsPage(props: IProps) {
    const classes = useStyles();
    const { className, ...rest } = props;

    const [newsArticles, setNewsArticles] = useState<INewsArticle[]>([]);
    const [isLoadingNewsArticles, setIsLoadingNewsArticles] = useState<boolean>(true);
    const [isError, setIsErrror] = useState<boolean>(false);

    const [totalNumberOfArticles, setTotalNumberOfArticles] = useState<number>();

    const [currentPage, setCurrentPage] = useState<number>(0);
    const [isLoadingMoreArticles, setIsLoadingMoreArticles] = useState<boolean>(false);

    const pageSize = 7;

    useEffect(() => {
        window.scrollTo(0, 0);
        setIsLoadingNewsArticles(true);
        newsApi
            .getAllArticles({ pageOffset: 0, pageSize: pageSize })
            .then((newsResponse) => {
                setTotalNumberOfArticles(newsResponse.total);
                setNewsArticles(newsResponse.news);
                setIsErrror(false);
            })
            .catch((err) => {
                setIsErrror(true);
            })
            .finally(() => {
                setIsLoadingNewsArticles(false);
            });
    }, []);

    const loadNextPageOfNews = () => {
        if (isLoadingNewsArticles || isLoadingMoreArticles) return;

        const nextPage = currentPage + 1;

        setIsLoadingMoreArticles(true);
        newsApi
            .getAllArticles({ pageOffset: nextPage, pageSize: pageSize })
            .then((newsResponse) => {
                setTotalNumberOfArticles(newsResponse.total);
                setNewsArticles([...newsArticles, ...newsResponse.news]);
                setIsErrror(false);
                setCurrentPage(nextPage);
            })
            .catch((err) => {
                setIsErrror(true);
            })
            .finally(() => {
                setIsLoadingMoreArticles(false);
            });
    };

    return (
        <PageSection className={clsx(className, classes.root)} {...rest}>
            <div className={classes.content}>
                <Spacer size={3} />
                {isError ? (
                    <Typography variant="body2">
                        There has been an error fetching the news. Please try again.
                    </Typography>
                ) : newsArticles?.length === 0 && !isLoadingNewsArticles ? (
                    <Typography>No recent news</Typography>
                ) : (
                    <>
                        <Rotate amount={-1}>
                            <ArticleOverviewWithDescription
                                article={
                                    typeof newsArticles === "undefined"
                                        ? undefined
                                        : newsArticles[0]
                                }
                                isLoading={isLoadingNewsArticles}
                            />
                        </Rotate>

                        {newsArticles.length > 1 ? (
                            <>
                                <Spacer size={4} />
                                <div className={classes.subArticleContainer}>
                                    {newsArticles.map((article, i) => {
                                        if (i === 0)
                                            return <React.Fragment key={i}></React.Fragment>;

                                        const rotateDirection = i % 3 === 0 || i % 3 === 1 ? -1 : 1;
                                        const rotateAmount = i % 2 === 0 ? 0.5 : 1;

                                        return (
                                            <React.Fragment key={i}>
                                                <Rotate
                                                    amount={rotateDirection * rotateAmount}
                                                    className={classes.subArticle}
                                                >
                                                    <SubArticleWithTitle article={article} />
                                                </Rotate>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                                {typeof totalNumberOfArticles ===
                                "undefined" ? null : totalNumberOfArticles > newsArticles.length ? (
                                    <>
                                        <Spacer size={4} />
                                        <div className={classes.loadMoreButtonContainer}>
                                            <LoadableButton
                                                onClick={() => loadNextPageOfNews()}
                                                isLoading={isLoadingMoreArticles}
                                            >
                                                Load More
                                            </LoadableButton>
                                        </div>
                                    </>
                                ) : null}
                            </>
                        ) : null}
                    </>
                )}
            </div>
        </PageSection>
    );
}
